import { DatePicker, Select, Spin } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { allAis } from 'utils/functionsUtils';
import { generateDataLineChart } from '../helpers';
import moment, { Moment } from 'moment';
import { toast } from 'react-toastify';
import { chartsService } from 'services/modulos/chartsService';

const { Option } = Select;

const VehiclesByTimeChart = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAis, setSelectedAis] = useState('AIS01');
  const [selectedDate, setSelectedDate] = useState<Moment | null>(moment());

  const [dataAPI, setDataAPI] = useState<{
    empenhadas: ReturnTotalVtrHora[];
    total: ReturnTotalVtrHora[];
  }>();

  const [data, setData] = useState<
    {
      hour: string;
      purple: number;
      orange: number;
    }[]
  >([]);

  const handleChangeFilters = useCallback(
    (field: string, value: string) => {
      if (field === 'ais') {
        setSelectedAis(value);
      }

      const newData = generateDataLineChart({
        total: dataAPI?.total || [],
        empenhadas: dataAPI?.empenhadas || [],
        ais: value,
      });

      setData(newData || []);
    },
    [dataAPI]
  );

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!selectedDate) {
        toast.info('Informe a data da pesquisa');
        return;
      }

      const simpleDataBody = {
        data: selectedDate.format('YYYY-MM-DD'),
      };

      const dataBody = {
        dataInicio: selectedDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        dataFim: selectedDate.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      };

      const [empenhadas, total] = await Promise.all([
        chartsService.getVtrEmpenhadasByHour(dataBody),
        chartsService.getTotalVtrHora(simpleDataBody),
      ]);

      const newData = generateDataLineChart({
        total,
        empenhadas,
        ais: selectedAis,
      });

      setDataAPI({
        empenhadas,
        total,
      });
      setData(newData || []);
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, selectedAis]);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  return (
    <div
      style={{
        width: '100%',
        height: 500,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '50px',
      }}
    >
      <h1 style={{ font: 'bold' }}>Viaturas conectadas por hora</h1>

      <div className="bar-chart-filter">
        <div className="default-flex-div">
          <label
            style={{
              fontWeight: '600',
              color: 'grey',
            }}
          >
            Data:
          </label>
          <DatePicker
            value={selectedDate}
            onChange={e => setSelectedDate(e)}
            format="YYYY-MM-DD"
          />
        </div>

        <div className="default-flex-div">
          <label
            style={{
              fontWeight: '600',
              color: 'grey',
            }}
          >
            AIS:
          </label>

          <Select
            value={selectedAis}
            onChange={e => handleChangeFilters('ais', e)}
            showSearch
            style={{ width: 200 }}
            virtual={false}
            // mode="tags"
            notFoundContent={null}
            getPopupContainer={trigger => trigger.parentElement}
            placeholder="AIS"
            filterOption={(input, option) => {
              return Boolean(
                option?.children &&
                  String(option.children || '')
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              );
            }}
          >
            {allAis.map(ais => (
              <Option key={ais.value} value={ais.value}>
                {ais.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      {isLoading && (
        <div
          style={{
            position: 'absolute',
            opacity: '50%',
            backgroundColor: '#d3d3d3',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin />
        </div>
      )}

      <ResponsiveContainer>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="hour" />
          <YAxis domain={[0, 20]} />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="orange"
            stroke="orange"
            strokeWidth={2}
            dot={false}
            name="Total de viaturas"
          />
          <Line
            type="monotone"
            dataKey="purple"
            stroke="purple"
            strokeWidth={2}
            dot={false}
            name="Viaturas empenhadas"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default VehiclesByTimeChart;
